/**
 * Formats any technical error, Firestore error, Firebase error, or exception
 * into a clean, friendly, action-oriented message for end users.
 * Strips away all raw JSON, database internals, paths, and technical error objects.
 */
export function formatUserFacingError(err: unknown, fallbackMessage?: string): string {
  if (!err && !fallbackMessage) {
    return 'Der opstod en uventet fejl. Prøv venligst igen.';
  }

  let rawString = '';

  if (typeof err === 'string') {
    rawString = err;
  } else if (err instanceof Error) {
    rawString = err.message || err.name || String(err);
  } else if (err && typeof err === 'object') {
    // If it has a message property
    if ('message' in err && typeof (err as any).message === 'string') {
      rawString = (err as any).message;
    } else if ('error' in err && typeof (err as any).error === 'string') {
      rawString = (err as any).error;
    } else {
      try {
        rawString = JSON.stringify(err);
      } catch {
        rawString = String(err);
      }
    }
  } else {
    rawString = String(err);
  }

  const lower = rawString.toLowerCase();

  // 1. Permission / Access Rights Errors
  if (
    lower.includes('missing or insufficient permissions') ||
    lower.includes('permission-denied') ||
    lower.includes('permission denied') ||
    lower.includes('insufficient permissions') ||
    lower.includes('unauthorized') ||
    lower.includes('forbidden')
  ) {
    if (fallbackMessage) {
      const cleanPrefix = fallbackMessage.replace(/:\s*$/, '').trim();
      return `${cleanPrefix}. Du har ikke de nødvendige rettigheder til denne handling.`;
    }
    return 'Du har ikke de nødvendige tilladelser til at udføre denne handling. Kontakt venligst din virksomhedsadministrator.';
  }

  // 2. Authentication & Session Errors
  if (
    lower.includes('unauthenticated') ||
    lower.includes('auth/user-not-found') ||
    lower.includes('auth/wrong-password') ||
    lower.includes('auth/invalid-credential') ||
    lower.includes('auth/invalid-login-credentials')
  ) {
    return 'Forkert e-mailadresse eller adgangskode. Tjek venligst dine oplysninger og prøv igen.';
  }

  if (lower.includes('auth/email-already-in-use') || lower.includes('already-exists')) {
    return 'Der findes allerede en bruger eller enhed med disse oplysninger.';
  }

  if (lower.includes('auth/weak-password')) {
    return 'Adgangskoden er for svag. Vælg venligst en stærkere adgangskode med mindst 6 tegn.';
  }

  if (lower.includes('auth/popup-closed-by-user') || lower.includes('popup-closed')) {
    return 'Handlingen blev annulleret, da pop-up-vinduet blev lukket.';
  }

  if (lower.includes('auth/requires-recent-login')) {
    return 'Af sikkerhedsmæssige årsager bedes du logge ud og ind igen for at gennemføre denne handling.';
  }

  // 3. Network & Connection Errors
  if (
    lower.includes('network') ||
    lower.includes('offline') ||
    lower.includes('failed to fetch') ||
    lower.includes('unavailable') ||
    lower.includes('timeout') ||
    lower.includes('the client is offline')
  ) {
    return 'Der kunne ikke oprettes forbindelse til serveren. Tjek din internetforbindelse og prøv igen.';
  }

  // 4. Not Found Errors
  if (lower.includes('not-found') || lower.includes('not found') || lower.includes('does not exist')) {
    return 'Det ønskede element blev ikke fundet. Prøv at opdatere siden.';
  }

  // 5. JSON / Database Path Payload Detection
  if (
    rawString.startsWith('{') ||
    rawString.includes('"error"') ||
    rawString.includes('"authInfo"') ||
    rawString.includes('"operationType"') ||
    rawString.includes('"path"') ||
    rawString.includes('firestore')
  ) {
    // Check if rawString embeds a clean, user-facing error message (e.g. credit/subscription warnings)
    try {
      const jsonStart = rawString.indexOf('{');
      const jsonEnd = rawString.lastIndexOf('}');
      if (jsonStart !== -1 && jsonEnd > jsonStart) {
        const parsed = JSON.parse(rawString.slice(jsonStart, jsonEnd + 1));
        const innerErr = parsed.error || parsed.message;
        if (typeof innerErr === 'string' && innerErr.trim().length > 0) {
          const innerLower = innerErr.toLowerCase();
          if (
            innerLower.includes('credit') ||
            innerLower.includes('abonnement') ||
            innerLower.includes('udløbet') ||
            innerLower.includes('kvik go') ||
            innerLower.includes('grænse') ||
            (!innerLower.includes('permission-denied') && !innerLower.includes('missing or insufficient') && !innerLower.includes('internal') && innerErr.length < 350)
          ) {
            return innerErr.trim();
          }
        }
      }
    } catch (_) {}

    if (fallbackMessage) {
      return fallbackMessage.replace(/:\s*$/, '').trim() + '. Prøv venligst igen senere.';
    }
    return 'Handlingen kunne ikke gennemføres. Tjek venligst dine indstillinger og prøv igen.';
  }

  // 6. If the raw string is cleanly user-friendly (does not contain stack traces or code symbols)
  // Check if rawString has technical syntax
  const isTechnical = /[\{\}\[\]\<\>\\\/]|error code:|stack trace|uncaught/i.test(rawString);
  if (!isTechnical && rawString.trim().length > 0 && rawString.length < 250) {
    return rawString.trim();
  }

  if (fallbackMessage) {
    return fallbackMessage.replace(/:\s*$/, '').trim();
  }

  return 'Der opstod en fejl under udførelsen. Prøv venligst igen senere.';
}

export const formatError = formatUserFacingError;
